<script lang="ts">
  import type { Duel } from '$types/index';
  import DuelStatus from './DuelStatus.svelte';
  import VoteModeChip from './VoteModeChip.svelte';
  import Countdown from './Countdown.svelte';
  import { phaseEnd } from '$lib/utils/duel-phase';
  import { onSwipe } from '$lib/utils/swipe';
  import { navigate } from '$lib/router.svelte';

  let {
    duel,
    ondismiss,
    mySongBadge = null,
  }: {
    duel: Duel;
    ondismiss?: (id: number) => void;
    /** Home-feed creator lens — "Your song is in" / "Your song WON" (gold). */
    mySongBadge?: { label: string; won?: boolean } | null;
  } = $props();

  // Web is view-only for duels: cards link to the detail (or reveal) page.
  const playHref = $derived(duel.status === 'ended' ? `#/duels/${duel.id}/result` : `#/duels/${duel.id}`);

  // Prompt/global themes lead with their editorial prompt line.
  const showPrompt = $derived((duel.theme_type === 'prompt' || duel.theme_type === 'global') && !!duel.prompt_text);

  // Meta: genre·style for themed duels, "Global" for the global pool.
  const themeMeta = $derived(
    [duel.genre_name, duel.style].filter(Boolean).join(' · ') || (duel.theme_type === 'global' ? 'Global' : ''),
  );

  const countdownEnd = $derived(phaseEnd(duel));

  // Swipe state — 'left' = dismiss, 'right' = engage (navigate to detail/result)
  let cardEl: HTMLAnchorElement | undefined = $state();
  let swipeState = $state<'left' | 'right' | null>(null);

  function isMobile(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
  }

  $effect(() => {
    if (!cardEl || !isMobile()) return;
    return onSwipe(cardEl, {
      onLeft: () => {
        if (swipeState) return;
        swipeState = 'left';
        // Animate off-screen, then notify parent. 250ms matches the CSS transition.
        setTimeout(() => ondismiss?.(duel.id), 250);
      },
      onRight: () => {
        if (swipeState) return;
        swipeState = 'right';
        // navigate() prepends its own leading `#`, so pass the path WITHOUT the
        // `playHref` leading `#` (that prefix is only correct for the <a href> below) —
        // otherwise this produces `##/duels/{id}` and 404s.
        setTimeout(() => navigate(playHref.slice(1)), 250);
      },
    });
  });
</script>

<a
  bind:this={cardEl}
  href={playHref}
  class="card duel-card"
  class:duel-card--swipe-left={swipeState === 'left'}
  class:duel-card--swipe-right={swipeState === 'right'}
>
  <div class="duel-card__info">
    <div class="duel-card__top">
      <h3 class="duel-card__name">{duel.name}</h3>
      <DuelStatus status={duel.status} />
    </div>
    {#if showPrompt}
      <p class="duel-card__prompt">{duel.prompt_text}</p>
    {/if}
    <p class="duel-card__meta">
      {#if themeMeta}<span class="duel-card__theme">{themeMeta}</span>{/if}
      <VoteModeChip mode={duel.vote_mode} />
    </p>
    <div class="duel-card__stats">
      <span
        >{duel.entry_count ?? 0}{duel.entry_cap ? `/${duel.entry_cap}` : ''}
        {(duel.entry_count ?? 0) === 1 && !duel.entry_cap ? 'entry' : 'entries'}</span
      >
      {#if countdownEnd}
        <span class="countdown"><Countdown end={countdownEnd} /></span>
      {/if}
    </div>
    {#if mySongBadge}
      <div class="duel-card__actions">
        <span class="badge" class:badge--green={!mySongBadge.won} class:duel-card__badge--won={mySongBadge.won}
          >{mySongBadge.label}</span
        >
      </div>
    {/if}
  </div>
</a>

<style>
  .duel-card {
    display: block;
    transition:
      transform 0.25s ease-out,
      opacity 0.25s ease-out;
    will-change: transform, opacity;
  }
  .duel-card--swipe-left {
    transform: translateX(-110%) rotate(-4deg);
    opacity: 0;
    pointer-events: none;
  }
  .duel-card--swipe-right {
    transform: translateX(110%) rotate(4deg);
    opacity: 0;
    pointer-events: none;
  }
  .duel-card__info {
    min-width: 0;
  }
  .duel-card__top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .duel-card__name {
    font-weight: 600;
    line-height: 1.3;
    margin: 0;
  }
  .duel-card__prompt {
    margin: 0.375rem 0 0;
    font-size: 0.875rem;
    font-style: italic;
    color: var(--text-secondary);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .duel-card__meta {
    margin: 0.5rem 0 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
  .duel-card__stats {
    margin-top: 0.625rem;
    display: flex;
    gap: 1rem;
    font-size: 0.75rem;
    color: var(--text-muted);
    flex-wrap: wrap;
  }
  .countdown {
    color: var(--cyan);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  .duel-card__actions {
    margin-top: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .duel-card__badge--won {
    color: var(--gold);
    border-color: rgba(255, 215, 0, 0.45);
  }
</style>
